// src/components/admin_panel/AdminReferralAnalytics.js
import React, { useState, useEffect } from "react";

const AdminReferralAnalytics = ({ adminToken }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchAnalytics();
  }, [adminToken]);

  const fetchAnalytics = async () => {
    if (!adminToken) return;

    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/admin/referral/analytics", {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      console.error("Error fetching referral analytics:", err);
      setError("Failed to load referral analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div style={{ fontSize: "18px", color: "#666" }}>Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "40px" }}>
        <div style={{
          background: "#fee2e2",
          border: "1px solid #ef4444",
          borderRadius: "8px",
          padding: "16px",
          color: "#991b1b"
        }}>
          {error}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div style={{ fontSize: "18px", color: "#666" }}>No data available</div>
      </div>
    );
  }

  const conversionRate = analytics.conversion_rate_percent || 0;

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ marginBottom: "30px", color: "#111827" }}>
        🎁 Referral Program Analytics
      </h2>

      {/* Summary Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "20px",
        marginBottom: "40px"
      }}>
        <div style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            Total Referrals
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {analytics.total_referrals_created}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
          color: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(240, 147, 251, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            Trials Started
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {analytics.trials_started}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
          color: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(250, 112, 154, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            Converted to Paid
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {analytics.converted_to_paid}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
          color: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(48, 207, 208, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            Conversion Rate
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {conversionRate.toFixed(1)}%
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
          color: "#111827",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(168, 237, 234, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.7, marginBottom: "8px" }}>
            Currently Active
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {analytics.currently_active_referrals}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
          color: "#111827",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(246, 211, 101, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.7, marginBottom: "8px" }}>
            Total Credits Issued
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            £{parseFloat(analytics.total_credits_issued || 0).toFixed(2)}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
          color: "#111827",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(132, 250, 176, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.7, marginBottom: "8px" }}>
            Total Credits Applied
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            £{parseFloat(analytics.total_credits_applied || 0).toFixed(2)}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
          color: "#111827",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(255, 154, 158, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.7, marginBottom: "8px" }}>
            Credits Remaining
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            £{(parseFloat(analytics.total_credits_issued || 0) - parseFloat(analytics.total_credits_applied || 0)).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div style={{
        background: "white",
        borderRadius: "12px",
        padding: "24px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
      }}>
        <h3 style={{ marginBottom: "20px", color: "#111827" }}>
          🏆 Top Referrers Leaderboard
        </h3>

        {analytics.leaderboard && analytics.leaderboard.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px"
            }}>
              <thead>
                <tr style={{
                  background: "#f9fafb",
                  borderBottom: "2px solid #e5e7eb"
                }}>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Rank</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>User</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Email</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Referral Code</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Total Referrals</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Trials Started</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Converted</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Active</th>
                  <th style={{ padding: "12px", textAlign: "right", fontWeight: "600" }}>Credits Earned</th>
                </tr>
              </thead>
              <tbody>
                {analytics.leaderboard.map((entry, index) => (
                  <tr key={entry.user_id} style={{
                    borderBottom: "1px solid #e5e7eb",
                    background: index < 3 ? "#fef3c7" : "white"
                  }}>
                    <td style={{ padding: "12px" }}>
                      {index === 0 && "🥇"}
                      {index === 1 && "🥈"}
                      {index === 2 && "🥉"}
                      {index > 2 && `#${index + 1}`}
                    </td>
                    <td style={{ padding: "12px", fontWeight: "500" }}>
                      {entry.first_name} {entry.last_name}
                    </td>
                    <td style={{ padding: "12px", color: "#666" }}>
                      {entry.email}
                    </td>
                    <td style={{
                      padding: "12px",
                      textAlign: "center",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      color: "#8B5FBF",
                      fontWeight: "600"
                    }}>
                      {entry.referral_code}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {entry.total_referrals}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {entry.trials_started}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center", color: "#10b981", fontWeight: "600" }}>
                      {entry.successful_conversions}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center", color: "#f59e0b", fontWeight: "600" }}>
                      {entry.active_referrals}
                    </td>
                    <td style={{ padding: "12px", textAlign: "right", fontWeight: "600", color: "#667eea" }}>
                      £{parseFloat(entry.total_credits_earned).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{
            padding: "40px",
            textAlign: "center",
            color: "#9ca3af"
          }}>
            No referrers yet
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div style={{ marginTop: "30px", textAlign: "center" }}>
        <button
          onClick={fetchAnalytics}
          style={{
            background: "#667eea",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)"
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#5568d3"}
          onMouseLeave={(e) => e.currentTarget.style.background = "#667eea"}
        >
          🔄 Refresh Analytics
        </button>
      </div>
    </div>
  );
};

export default AdminReferralAnalytics;
